import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/Icon";
import { PageHero } from "@/components/PublicLayout";
import { TextField, TextAreaField, SelectField, CheckField } from "@/components/FormKit";
import { SuccessScreen } from "@/pages/BookRide";
import { useSite, mediaUrl } from "@/context/SiteContext";
import { FACILITY_BENEFITS, IMAGES } from "@/lib/data";

const empty = {
  organization_name: "", contact_name: "", job_title: "", phone: "", email: "",
  facility_address: "", organization_type: "", trips_per_week: "", transportation_needs: "",
  counties_needed: "", ambulatory_needed: false, wheelchair_needed: false,
  recurring_needed: false, additional_info: "",
};

export default function ForFacilities() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const content = useSite();
  const facilitiesSrc = content.facilities_image_id ? mediaUrl(content.facilities_image_id) : IMAGES.facilities;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setE = (k) => (e) => set(k)(e.target.value);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.organization_name || !form.contact_name || !form.phone || !form.email) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/facility-leads", form);
      setDone(true);
      toast.success("Partnership request submitted!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally { setLoading(false); }
  };

  if (done) return <SuccessScreen title="Partnership Request Received" message="Thank you! A CareDeserved representative will reach out to discuss a transportation partnership tailored to your organization." />;

  return (
    <>
      <PageHero eyebrow="For Facilities & Partners" title="A Transportation Partner You Can Depend On"
        subtitle="CareDeserved works with healthcare facilities, treatment centers, senior communities, day programs, agencies, case managers, and other organizations that need dependable passenger transportation." />

      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="overflow-hidden rounded-3xl shadow-xl">
              <img src={facilitiesSrc} alt="Healthcare facility exterior" className="h-[340px] w-full object-cover" loading="lazy" />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 sm:text-3xl">Why organizations partner with us</h2>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {FACILITY_BENEFITS.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <Icon name="CheckCircle" size={20} weight="fill" className="mt-0.5 shrink-0 text-brand-teal" /> {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-brand-bg py-16 sm:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <h2 className="font-heading text-2xl font-bold text-slate-900 sm:text-3xl">Request Partnership Information</h2>
            <p className="mt-2 text-slate-600">Tell us about your organization and transportation needs.</p>
          </div>
          <form onSubmit={submit} data-testid="facility-form" className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
            <div className="grid gap-5 sm:grid-cols-2">
              <TextField label="Organization Name" required testid="fac-org" wrapClass="sm:col-span-2" value={form.organization_name} onChange={setE("organization_name")} />
              <TextField label="Contact Name" required testid="fac-contact" value={form.contact_name} onChange={setE("contact_name")} />
              <TextField label="Job Title" testid="fac-title" value={form.job_title} onChange={setE("job_title")} />
              <TextField label="Phone" required testid="fac-phone" type="tel" value={form.phone} onChange={setE("phone")} />
              <TextField label="Email" required testid="fac-email" type="email" value={form.email} onChange={setE("email")} />
              <TextField label="Facility Address" testid="fac-address" wrapClass="sm:col-span-2" value={form.facility_address} onChange={setE("facility_address")} />
              <SelectField label="Type of Organization" testid="fac-type" value={form.organization_type} onValueChange={set("organization_type")}
                placeholder="Select type" options={["Hospital", "Treatment Center", "Senior Community", "Day Program", "Agency", "Case Management", "Other"]} />
              <TextField label="Estimated Trips Per Week" testid="fac-trips" value={form.trips_per_week} onChange={setE("trips_per_week")} />
              <TextField label="Counties Needed" testid="fac-counties" wrapClass="sm:col-span-2" value={form.counties_needed} onChange={setE("counties_needed")} placeholder="e.g. Berks, Lehigh, Lancaster" />
              <TextAreaField label="Transportation Needs" testid="fac-needs" wrapClass="sm:col-span-2" value={form.transportation_needs} onChange={setE("transportation_needs")} />
              <div className="grid gap-3 sm:col-span-2 sm:grid-cols-3">
                <CheckField label="Ambulatory Needed" testid="fac-ambulatory" checked={form.ambulatory_needed} onCheckedChange={set("ambulatory_needed")} />
                <CheckField label="Wheelchair Needed" testid="fac-wheelchair" checked={form.wheelchair_needed} onCheckedChange={set("wheelchair_needed")} />
                <CheckField label="Recurring Transportation" testid="fac-recurring" checked={form.recurring_needed} onCheckedChange={set("recurring_needed")} />
              </div>
              <TextAreaField label="Additional Information" testid="fac-info" wrapClass="sm:col-span-2" value={form.additional_info} onChange={setE("additional_info")} />
            </div>
            <Button type="submit" disabled={loading} data-testid="fac-submit"
              className="mt-6 h-14 w-full rounded-full bg-brand-blue text-base font-semibold text-white hover:bg-brand-blue2">
              {loading ? "Submitting…" : "Request Partnership Information"}
            </Button>
          </form>
        </div>
      </section>
    </>
  );
}
